import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { speakWelcome } from "../lib/voice";
import { toast } from "sonner";

const LOGO = "/tresses_logo_full.png";

export default function RegisterPage() {
    const { register, formatApiError } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const nextRaw = params.get("next") || "";
    const next = nextRaw.startsWith("/") && !nextRaw.startsWith("//") ? nextRaw : "";
    const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", address_line: "", city: "", postal_code: "", country: "Singapore", referral_code: "" });
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        // Validate SG phone (8 digits, starts with 6/8/9)
        const phoneDigits = (form.phone || "").replace(/\D/g, "");
        if (!(phoneDigits.length === 8 && /^[689]/.test(phoneDigits))) {
            toast.error("Enter a valid 8-digit Singapore phone number starting with 6, 8, or 9");
            return;
        }
        setSubmitting(true);
        try {
            const data = await register({ ...form, phone: `+65${phoneDigits}` });
            speakWelcome(data.user.name);
            toast.success(`Welcome to Tresses, ${data.user.name}`);
            navigate(next || "/dashboard");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[#FBF6F0]" data-testid="register-page">
            <div className="hidden lg:block relative grain" style={{
                backgroundImage: "url(https://images.pexels.com/photos/3738359/pexels-photo-3738359.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900)",
                backgroundSize: "cover",
                backgroundPosition: "center"
            }}>
                <div className="absolute inset-0 bg-gradient-to-t from-[#FBF6F0] via-[#FBF6F0]/40 to-transparent" />
                <div className="absolute bottom-12 left-12 right-12">
                    <h2 className="font-serif-display text-5xl text-[#3D0C1F] leading-tight">
                        Begin your<br /><span className="italic gold-text">ritual</span>.
                    </h2>
                </div>
            </div>
            <div className="flex items-center justify-center px-6 py-12 grain">
                <div className="w-full max-w-md">
                    <Link to="/" className="flex items-center gap-4 mb-12">
                        <img src={LOGO} alt="The Tresses — Unisex Salon" className="h-36 w-auto object-contain" data-testid="auth-logo" />
                    </Link>
                    <div className="text-overline">Create account</div>
                    <h1 className="font-serif-display text-4xl text-[#3D0C1F] mt-3">Join Tresses.</h1>
                    <p className="text-sm text-[#7A5262] mt-2">Earn loyalty points from your very first visit.</p>

                    <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="register-form">
                        <div>
                            <label className="text-overline">Full Name</label>
                            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="register-name" />
                        </div>
                        <div>
                            <label className="text-overline">Email</label>
                            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="register-email" />
                        </div>
                        <div>
                            <label className="text-overline">Password</label>
                            <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                data-testid="register-password" />
                        </div>
                        <div>
                            <label className="text-overline">Phone</label>
                            <div className="flex mt-2">
                                <span className="inline-flex items-center px-3 bg-[#FBF6F0] border border-r-0 border-[#6B1535]/15 text-[#8F622C] text-sm font-medium">+65</span>
                                <input
                                    type="tel"
                                    required
                                    inputMode="numeric"
                                    maxLength={8}
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                                    className="flex-1 bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 focus:border-[#C29A5B] outline-none text-[#3D0C1F] tracking-wider"
                                    placeholder="9123 4567"
                                    data-testid="register-phone"
                                />
                            </div>
                            <p className="text-[0.65rem] text-[#8A6574] mt-1">8 digits, must start with 6, 8, or 9</p>
                        </div>
                        <div className="pt-2">
                            <div className="text-overline mb-3">Address <span className="normal-case tracking-normal text-[#8A6574] text-[0.7rem]">— optional, can add later</span></div>
                            <input value={form.address_line} onChange={(e) => setForm({ ...form, address_line: e.target.value })}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                placeholder="Address line"
                                data-testid="register-address-line" />
                            <div className="grid grid-cols-2 gap-3 mt-3">
                                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                                    className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                    placeholder="City"
                                    data-testid="register-city" />
                                <input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                                    className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                    placeholder="Postal code"
                                    data-testid="register-postal" />
                            </div>
                            <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-3 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                placeholder="Country"
                                data-testid="register-country" />
                        </div>
                        <div>
                            <label className="text-overline">Referral Code (optional)</label>
                            <input value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                                className="w-full bg-[#FDF0EC] border border-[#6B1535]/15 px-4 py-3 mt-2 focus:border-[#C29A5B] outline-none text-[#3D0C1F]"
                                placeholder="A friend's code — both earn 100 pts when you complete your first service" data-testid="register-referral" />
                        </div>
                        <button type="submit" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-50" data-testid="register-submit">
                            {submitting ? "Creating…" : "Create Account"}
                        </button>
                    </form>
                    <p className="text-sm text-[#7A5262] mt-8 text-center">
                        Already a member?{" "}
                        <Link to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="text-[#8F622C] hover:text-[#3D0C1F]" data-testid="register-to-login">Sign in</Link>
                    </p>
                    <div className="text-center mt-6 pt-6 border-t border-[#6B1535]/10">
                        <Link to="/" className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] hover:text-[#8F622C]" data-testid="register-back-home">
                            ← Back to home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
